import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Title,
  TextInput,
  Autocomplete,
  Textarea,
  Select,
  Button,
  Stack,
  Group,
  Text,
  Badge,
  Divider,
  Modal,
  ThemeIcon,
} from '@mantine/core';
import { Link } from 'react-router-dom';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { lostClaimsApi } from '../api/lostClaims';
import { autocompleteApi } from '../api/autocomplete';
import { categoriesApi } from '../api/categories';
import { usePendingImages } from '../hooks/usePendingImages';
import { buildCategorySelectData } from '../utils/categories';
import { buildColorSynonymMap, buildCanonicalBrands, buildCategorySynonymMap, detectFromText } from '../utils/detectFromText';
import { ImageUpload } from '../components/ImageUpload';
import type { CreateClaimInput } from '../types';
import { pageContainer } from '../styles/theme.css';
import { getSemester } from '../utils/semester';

const CONTACT_METHODS = [
  { label: 'Telefon', value: 'phone' },
  { label: 'Email', value: 'email' },
  { label: 'Instagram', value: 'instagram' },
  { label: 'Facebook', value: 'facebook' },
  { label: 'Andet', value: 'other' },
];

export function LostClaimForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { pending, previewImages, addImage, deleteImage } = usePendingImages();
  const [autoFilled, setAutoFilled] = useState<{ color: boolean; brand: boolean; category: boolean }>({ color: false, brand: false, category: false });
  const [matchModal, setMatchModal] = useState<{ claimId: number; count: number } | null>(null);

  const { data: acData } = useQuery({
    queryKey: ['autocomplete'],
    queryFn: autocompleteApi.get,
    staleTime: 60_000,
  });

  const { data: catData } = useQuery({
    queryKey: ['categories'],
    queryFn: categoriesApi.get,
    staleTime: Infinity,
  });

  const colors = acData?.colors ?? [];
  const brands = acData?.brands ?? [];
  const titles = acData?.titles ?? [];

  const categorySelectData = buildCategorySelectData(catData);
  const colorBySynonym = buildColorSynonymMap(catData);
  const canonicalBrands = buildCanonicalBrands(catData);
  const categoryBySynonym = buildCategorySynonymMap(catData);

  const form = useForm<CreateClaimInput>({
    initialValues: {
      description: '',
      category_id: null,
      color: null,
      brand: null,
      distinctive_features: null,
      lost_date: null,
      contact_name: '',
      contact_method: '',
      social_profile: null,
    },
    validate: {
      description: (v) => (v.trim() ? null : 'Beskrivelse er påkrævet'),
      contact_name: (v) => (v.trim() ? null : 'Navn er påkrævet'),
      contact_method: (v) => (v ? null : 'Kontaktmetode er påkrævet'),
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateClaimInput) => {
      const claim = await lostClaimsApi.create(data);
      await Promise.all(pending.map((p) => lostClaimsApi.addImage(claim.id, p.url, p.ziplineId)));
      return claim;
    },
    onSuccess: (claim) => {
      qc.invalidateQueries({ queryKey: ['lost-claims'] });
      qc.invalidateQueries({ queryKey: ['suggestions'] });
      if (claim.matches_found > 0) {
        setMatchModal({ claimId: claim.id, count: claim.matches_found });
      } else {
        notifications.show({ message: 'Efterlysning registreret', color: 'green' });
        navigate(`/lost-claims/${claim.id}`);
      }
    },
    onError: (err) => {
      notifications.show({ message: (err as Error).message, color: 'red' });
    },
  });


  function handleDescriptionChange(v: string) {
    form.setFieldValue('description', v);
    const { detectedColor, detectedBrand, detectedCategory } = detectFromText(v, colorBySynonym, canonicalBrands, categoryBySynonym);
    const canFillColor = !form.values.color || autoFilled.color;
    const canFillBrand = !form.values.brand || autoFilled.brand;
    const canFillCategory = !form.values.category_id || autoFilled.category;
    if (canFillColor) form.setFieldValue('color', detectedColor);
    if (canFillBrand) form.setFieldValue('brand', detectedBrand);
    if (canFillCategory) form.setFieldValue('category_id', detectedCategory ?? null);
    setAutoFilled({
      color: canFillColor && !!detectedColor,
      brand: canFillBrand && !!detectedBrand,
      category: canFillCategory && !!detectedCategory,
    });
  }

  return (
    <div className={pageContainer}>
      <Title order={2} mb="md">
        Ny efterlysning
      </Title>
      <Text size="sm" c="dimmed" mb="md">
        Udfyld formularen med så mange detaljer som muligt for at hjælpe med at finde genstanden.
      </Text>

      <form onSubmit={form.onSubmit((v) => createMutation.mutate(v))}>
        <Stack gap="sm">
          <Autocomplete
            label="Beskrivelse"
            placeholder="f.eks. Sort læderjakke"
            required
            data={form.values.description.length >= 2 ? titles : []}
            value={form.values.description}
            onChange={handleDescriptionChange}
            error={form.errors.description}
          />

          <Select
            label="Kategori"
            placeholder="Vælg kategori (valgfrit)"
            data={categorySelectData}
            searchable
            clearable
            description={autoFilled.category ? 'Autoudfyldt fra beskrivelse' : undefined}
            descriptionProps={autoFilled.category ? { style: { color: 'var(--mantine-color-red-4)' } } : undefined}
            {...form.getInputProps('category_id')}
            onChange={(v) => { form.setFieldValue('category_id', v); setAutoFilled((p) => ({ ...p, category: false })); }}
          />

          <Group grow>
            <Autocomplete
              label="Farve"
              placeholder="f.eks. Blå"
              data={(form.values.color ?? '').length >= 2 ? colors : []}
              value={form.values.color ?? ''}
              description={autoFilled.color ? 'Autoudfyldt fra beskrivelse' : undefined}
              descriptionProps={autoFilled.color ? { style: { color: 'var(--mantine-color-red-4)' } } : undefined}
              onChange={(v) => { form.setFieldValue('color', v || null); setAutoFilled((p) => ({ ...p, color: false })); }}
            />
            <Autocomplete
              label="Mærke"
              placeholder="f.eks. Samsung"
              data={(form.values.brand ?? '').length >= 2 ? brands : []}
              value={form.values.brand ?? ''}
              description={autoFilled.brand ? 'Autoudfyldt fra beskrivelse' : undefined}
              descriptionProps={autoFilled.brand ? { style: { color: 'var(--mantine-color-red-4)' } } : undefined}
              onChange={(v) => { form.setFieldValue('brand', v || null); setAutoFilled((p) => ({ ...p, brand: false })); }}
            />
          </Group>

          <Textarea
            label="Særlige kendetegn"
            placeholder="Særlige kendetegn"
            {...form.getInputProps('distinctive_features')}
          />

          <Stack gap={4}>
            <TextInput label="Mistet dato" type="date" {...form.getInputProps('lost_date')} />
            {form.values.lost_date && (
              <Badge size="sm" variant="light" color="blue" w="fit-content">
                {getSemester(form.values.lost_date)}
              </Badge>
            )}
          </Stack>

          <TextInput
            label="Navn"
            placeholder="Personens navn"
            required
            {...form.getInputProps('contact_name')}
          />

          <Select
            label="Kontaktmetode"
            placeholder="Vælg kontaktmetode"
            required
            data={CONTACT_METHODS}
            {...form.getInputProps('contact_method')}
          />

          <TextInput
            label="Socialt medie / kontaktoplysning"
            placeholder="f.eks. @ditbrugernavn eller +45 12 34 56 78"
            {...form.getInputProps('social_profile')}
          />

          <Divider label="Billeder" />

          <ImageUpload
            existingImages={previewImages}
            onUpload={addImage}
            onDelete={deleteImage}
          />

          <Button type="submit" fullWidth size="lg" mt="sm" loading={createMutation.isPending}>
            Send efterlysning
          </Button>
        </Stack>
      </form>

      <Modal
        opened={matchModal !== null}
        onClose={() => {
          if (matchModal) navigate(`/lost-claims/${matchModal.claimId}`);
          setMatchModal(null);
        }}
        withCloseButton={false}
        centered
        size="sm"
      >
        {matchModal && (
          <Stack align="center" gap="md" py="md">
            <ThemeIcon size={56} radius="xl" color="orange" variant="light">
              <Text size="xl">🔍</Text>
            </ThemeIcon>
            <Stack align="center" gap={4}>
              <Text fw={700} size="lg">Mulige match fundet!</Text>
              <Text c="dimmed" size="sm" ta="center">
                Efterlysningen matcher{' '}
                <b>{matchModal.count} fundet genstand{matchModal.count !== 1 ? 'e' : ''}</b>.
                Vil du se forslagene nu?
              </Text>
            </Stack>
            <Group>
              <Button
                component={Link}
                to="/matches"
                color="orange"
                onClick={() => setMatchModal(null)}
              >
                Se matches
              </Button>
              <Button
                variant="default"
                onClick={() => {
                  navigate(`/lost-claims/${matchModal.claimId}`);
                  setMatchModal(null);
                }}
              >
                Fortsæt
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </div>
  );
}
